import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { Warehouse } from '../../api/inventory';
import { fetchWarehouses, createWarehouseTransfer, fetchStockLevels } from '../../api/inventory';
import type { StockResponse, WarehouseStock, ItemStock } from '../../types/shared';

interface WarehouseTransferModalProps {
  isOpen:    boolean;
  onClose:   () => void;
  onSuccess: () => void;
}

export default function WarehouseTransferModal({ isOpen, onClose, onSuccess }: WarehouseTransferModalProps) {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [stockData,  setStockData]  = useState<StockResponse | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [destWarehouseId,   setDestWarehouseId]   = useState('');
  const [inventoryItemId,   setInventoryItemId]   = useState('');
  const [quantity, setQuantity] = useState<number>(0);
  const [notes,    setNotes]    = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [whData, stockLevels] = await Promise.all([
          fetchWarehouses(),
          fetchStockLevels(),
        ]);
        setWarehouses(whData);
        setStockData(stockLevels);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen]);

  // Flatten the region-grouped response to find a specific warehouse's stock
  const sourceStock: WarehouseStock | null = useMemo(() => {
    if (!stockData || !sourceWarehouseId) return null;
    for (const r of stockData.regions) {
      const w = r.warehouses.find(w => w.warehouseId === sourceWarehouseId);
      if (w) return w;
    }
    return null;
  }, [stockData, sourceWarehouseId]);

  // Items at the source warehouse with non-zero stock — only those can be transferred
  const transferableItems: ItemStock[] = useMemo(
    () => sourceStock?.items.filter(i => i.quantity > 0) ?? [],
    [sourceStock],
  );

  const selectedItem = transferableItems.find(i => i.inventoryItemId === inventoryItemId);
  const availableQty = selectedItem?.quantity ?? 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!sourceWarehouseId)                            return setError('Please select a source warehouse');
    if (!destWarehouseId)                              return setError('Please select a destination warehouse');
    if (sourceWarehouseId === destWarehouseId)         return setError('Source and destination warehouses must be different');
    if (!inventoryItemId)                              return setError('Please select an item to transfer');
    if (quantity <= 0)                                 return setError('Quantity must be greater than 0');
    if (quantity > availableQty)                       return setError('Quantity exceeds available stock');

    setSubmitting(true);
    try {
      await createWarehouseTransfer({
        sourceWarehouseId,
        destWarehouseId,
        inventoryItemId,
        quantity,
        notes: notes || undefined,
      });

      onSuccess();
      setSourceWarehouseId('');
      setDestWarehouseId('');
      setInventoryItemId('');
      setQuantity(0);
      setNotes('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create transfer');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-stone-900 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-stone-700 sticky top-0 bg-white dark:bg-stone-900">
          <h2 className="text-lg font-bold text-stone-900 dark:text-stone-100">Create Transfer</h2>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-400">
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-stone-100 dark:bg-stone-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-1.5">
                  From Warehouse
                </label>
                <select
                  value={sourceWarehouseId}
                  onChange={e => {
                    setSourceWarehouseId(e.target.value);
                    setDestWarehouseId('');
                    setInventoryItemId('');
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500"
                >
                  <option value="">Select warehouse...</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name} {w.code && `(${w.code})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Destination */}
              <div>
                <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-1.5">
                  To Warehouse
                </label>
                <select
                  value={destWarehouseId}
                  onChange={e => setDestWarehouseId(e.target.value)}
                  disabled={!sourceWarehouseId}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select warehouse...</option>
                  {warehouses
                    .filter(w => w.id !== sourceWarehouseId)
                    .map(w => (
                      <option key={w.id} value={w.id}>
                        {w.name} {w.code && `(${w.code})`}
                      </option>
                    ))}
                </select>
              </div>

              {/* Item */}
              <div>
                <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-1.5">
                  Item
                </label>
                <select
                  value={inventoryItemId}
                  onChange={e => setInventoryItemId(e.target.value)}
                  disabled={!sourceWarehouseId}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">{sourceWarehouseId ? 'Select item...' : 'Pick a source warehouse first'}</option>
                  {transferableItems.map(item => (
                    <option key={item.inventoryItemId} value={item.inventoryItemId}>
                      {item.itemName} ({item.quantity} available)
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-1.5">
                  Quantity
                  <span className="text-xs text-stone-500 dark:text-stone-400 font-normal ml-2">
                    (Available: {availableQty})
                  </span>
                </label>
                <input
                  type="number"
                  min="1"
                  max={availableQty}
                  value={quantity || ''}
                  onChange={e => setQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-stone-900 dark:text-stone-100 mb-1.5">
                  Notes (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="e.g., damaged units being returned"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 text-sm focus:outline-none focus:ring-2 focus:ring-moss-500"
                  rows={3}
                />
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4 border-t border-stone-200 dark:border-stone-700">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 text-sm font-medium hover:bg-stone-50 dark:hover:bg-stone-800 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || loading}
              className="flex-1 px-4 py-2 rounded-lg bg-moss-600 text-white text-sm font-medium hover:bg-moss-700 transition disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
